/* eslint-disable no-useless-escape */
/* eslint-disable consistent-return */
import {
    ErrorId,
    FieldId,
    FormValidationOptions,
    getSafe,
    overrideValidator,
} from '@sheerid/jslib';

const DECIMAL_RADIX = 10;
const ADOLESCENT_AGE = 18;
const MIN_BIRTHYEAR = 1900;
const MAX_BIRTHYEAR = 3000;

const isValidDate = (value: string): boolean => {
    const dateParts = value.split('-');
    const dateYear = parseInt(dateParts[0], DECIMAL_RADIX);
    const dateMonth = parseInt(dateParts[1], DECIMAL_RADIX);
    const dateDay = parseInt(dateParts[2], DECIMAL_RADIX);
    const monthDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (dateYear < MIN_BIRTHYEAR || dateYear > MAX_BIRTHYEAR || dateMonth <= 0 || dateMonth > 12) {
        return false;
    }

    // Check for leap years
    if (dateYear % 400 === 0 || (dateYear % 100 !== 0 && dateYear % 4 === 0)) {
        monthDays[1] = 29;
    }

    if (dateDay <= 0 || dateDay > monthDays[dateMonth - 1]) {
        return false;
    }
    return true;
};

const isValidDateFormat = (value: string): boolean => {
    const VALID_DATE_FORMAT_REGEXP = /^\d{4}[\-\s\/]\d{1,2}[\-\s\/]\d{1,2}$/;

    if (VALID_DATE_FORMAT_REGEXP.test(value)) {
        return true;
    }
    return false;
};

const isFutureDate = (value: string): boolean => {
    const currentDate = new Date();
    const candidateDate = new Date(value);
    return candidateDate > currentDate;
};

const isUnderageDate = (value: string, minAge = ADOLESCENT_AGE): boolean => {
    const birthDateParts = value.split('-');
    const birthDateYear = parseInt(birthDateParts[0], DECIMAL_RADIX);
    const birthDateMonth = parseInt(birthDateParts[1], DECIMAL_RADIX);
    const birthDateDay = parseInt(birthDateParts[2], DECIMAL_RADIX);
    const currentYear = (new Date()).getFullYear();
    const currentMonth = (new Date()).getMonth() + 1;
    const currentDay = (new Date()).getDate();

    if ((currentYear - birthDateYear) === minAge) {
        if ((currentMonth - birthDateMonth) < 0) {
            return true;
        }

        if ((currentMonth - birthDateMonth) === 0) {
            if ((currentDay - birthDateDay) < 0) {
                return true;
            }
        }
    } else if ((currentYear - birthDateYear) < minAge) {
        return true;
    }
    return false;
};

const isOverageDate = (value: string, maxAge = 120) => {
    const birthDateParts = value.split('-');
    const birthDateYear = parseInt(birthDateParts[0], DECIMAL_RADIX);
    const birthDateMonth = parseInt(birthDateParts[1], DECIMAL_RADIX);
    const birthDateDay = parseInt(birthDateParts[2], DECIMAL_RADIX);
    const currentYear = (new Date()).getFullYear();
    const currentMonth = (new Date()).getMonth() + 1;
    const currentDay = (new Date()).getDate();

    if ((currentYear - birthDateYear) === maxAge) {
        if ((currentMonth - birthDateMonth) > 0) {
            return true;
        }

        if ((currentMonth - birthDateMonth) === 0) {
            if ((currentDay - birthDateDay) > 0) {
                return true;
            }
        }
    } else if ((currentYear - birthDateYear) > maxAge) {
        return true;
    }
    return false;
};

const isAgeOutsideRange = (value: string, minAge: number, maxAge: number): boolean => isUnderageDate(value, minAge) || isOverageDate(value, maxAge);

export const addBirthDateValidator = () => {
    const birthDateFieldId: FieldId = 'birthDate';

    const getBirthDateValidationError = (value: string, formValidationOptions?: FormValidationOptions): ErrorId | undefined => {
        const invalidError: ErrorId = 'invalidBirthDate';
        const underagePersonError: ErrorId = 'underagePerson';
        const futureBirthDateError: ErrorId = 'futureBirthDate';
        const outsideAgeError: ErrorId = 'outsideAgePerson';

        if (value === undefined || !isValidDateFormat(value) || !isValidDate(value)) {
            return invalidError;
        }
        if (getSafe(() => formValidationOptions.minAge) && getSafe(() => formValidationOptions.maxAge)) {
            return isAgeOutsideRange(value, formValidationOptions.minAge, formValidationOptions.maxAge) ? outsideAgeError : null;
        }
        if (isFutureDate(value)) {
            return futureBirthDateError;
        }
        if (isUnderageDate(value)) {
            return underagePersonError;
        }
    };

    // @ts-expect-error
    overrideValidator(birthDateFieldId, getBirthDateValidationError);
};
